import React from 'react';
import type { NPCModelProps, NPCModelRegistry } from '../types';

const Camel = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <boxGeometry args={[0.3, 0.25, 0.5]} />
        <meshStandardMaterial
          color={world === 'real' ? '#C4A484' : '#8a7a6a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Humps */}
      {[-0.1, 0.1].map((z, i) => (
        <mesh key={i} position={[0, 0.55, z]} castShadow>
          <sphereGeometry args={[0.12, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color={world === 'real' ? '#C4A484' : '#8a7a6a'} />
        </mesh>
      ))}
      {/* Neck */}
      <mesh position={[0, 0.5, 0.3]} rotation={[-0.5, 0, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.08, 0.35, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#C4A484' : '#8a7a6a'} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.65, 0.45]} castShadow>
        <boxGeometry args={[0.1, 0.12, 0.15]} />
        <meshStandardMaterial color={world === 'real' ? '#C4A484' : '#8a7a6a'} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.62, 0.55]}>
        <boxGeometry args={[0.08, 0.08, 0.08]} />
        <meshStandardMaterial color={world === 'real' ? '#B8956A' : '#7a6a5a'} />
      </mesh>
      {/* Legs */}
      {[[-0.1, 0.35], [0.1, 0.35], [-0.1, -0.15], [0.1, -0.15]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.15, z]}>
          <cylinderGeometry args={[0.04, 0.05, 0.3, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#C4A484' : '#8a7a6a'} />
        </mesh>
      ))}
      {/* Ears */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.73, 0.42]}>
          <coneGeometry args={[0.02, 0.04, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#C4A484' : '#8a7a6a'} />
        </mesh>
      ))}
      {/* Eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.67, 0.5]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#2a2a2a" />
        </mesh>
      ))}
    </group>
  );
};

const Fennec = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.12, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#F5DEB3' : '#c0b090'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.22, 0.1]} castShadow>
        <sphereGeometry args={[0.07, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#F5DEB3' : '#c0b090'} />
      </mesh>
      {/* GIANT ears - signature fennec feature */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.38, 0.08]} rotation={[0.2, 0, (i - 0.5) * 0.3]}>
          <coneGeometry args={[0.04, 0.15, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#F5DEB3' : '#c0b090'} />
        </mesh>
      ))}
      {/* Inner ears */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.36, 0.09]} rotation={[0.2, 0, (i - 0.5) * 0.3]}>
          <coneGeometry args={[0.025, 0.1, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFB6C1' : '#c0a0a0'} />
        </mesh>
      ))}
      {/* Snout */}
      <mesh position={[0, 0.2, 0.16]}>
        <coneGeometry args={[0.03, 0.06, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#F5DEB3' : '#c0b090'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.19, 0.19]}>
        <sphereGeometry args={[0.012, 6, 6]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Eyes - large */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.24, 0.14]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color="#2a2a2a" />
        </mesh>
      ))}
      {/* Fluffy tail */}
      <mesh position={[0, 0.1, -0.15]} rotation={[0.8, 0, 0]}>
        <capsuleGeometry args={[0.04, 0.1, 4, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#F5DEB3' : '#c0b090'} />
      </mesh>
    </group>
  );
};

const Scorpion = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group scale={0.8}>
      {/* Body */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <boxGeometry args={[0.12, 0.05, 0.15]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2F1810' : '#3a2a3a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Tail segments */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} position={[0, 0.08 + i * 0.04, -0.1 - i * 0.05]} rotation={[-0.3 * (i + 1), 0, 0]}>
          <sphereGeometry args={[0.025 - i * 0.003, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#2F1810' : '#3a2a3a'} />
        </mesh>
      ))}
      {/* Stinger */}
      <mesh position={[0, 0.28, -0.32]} rotation={[-1.5, 0, 0]}>
        <coneGeometry args={[0.015, 0.05, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#1a0a05' : '#2a1a2a'} />
      </mesh>
      {/* Claws */}
      {[-0.1, 0.1].map((x, i) => (
        <group key={i} position={[x, 0.05, 0.1]}>
          <mesh>
            <boxGeometry args={[0.03, 0.03, 0.08]} />
            <meshStandardMaterial color={world === 'real' ? '#2F1810' : '#3a2a3a'} />
          </mesh>
          {/* Pincer */}
          <mesh position={[0, 0, 0.06]}>
            <boxGeometry args={[0.04, 0.02, 0.04]} />
            <meshStandardMaterial color={world === 'real' ? '#2F1810' : '#3a2a3a'} />
          </mesh>
        </group>
      ))}
      {/* Legs */}
      {[-1, 1].map((side) =>
        [0, 1, 2, 3].map((i) => (
          <mesh key={`${side}-${i}`} position={[side * 0.08, 0.03, 0.05 - i * 0.04]} rotation={[0, 0, side * 0.8]}>
            <cylinderGeometry args={[0.008, 0.006, 0.08, 4]} />
            <meshStandardMaterial color={world === 'real' ? '#2F1810' : '#3a2a3a'} />
          </mesh>
        ))
      )}
    </group>
  );
};

const Snake = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Coiled body */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = i * 1.2;
        const radius = 0.12 - i * 0.015;
        return (
          <mesh key={i} position={[Math.cos(angle) * radius, 0.03 + i * 0.025, Math.sin(angle) * radius]} castShadow>
            <sphereGeometry args={[0.04 - i * 0.004, 8, 8]} />
            <meshStandardMaterial
              color={world === 'real' ? '#8B7355' : '#5a4a4a'}
              emissive={highlightColor}
              emissiveIntensity={hovered ? 0.3 : 0}
            />
          </mesh>
        );
      })}
      {/* Head */}
      <mesh position={[0.08, 0.18, 0.08]} rotation={[0, -0.5, 0]} castShadow>
        <boxGeometry args={[0.06, 0.03, 0.08]} />
        <meshStandardMaterial color={world === 'real' ? '#8B7355' : '#5a4a4a'} />
      </mesh>
      {/* Eyes */}
      {[-0.015, 0.015].map((y, i) => (
        <mesh key={i} position={[0.12, 0.19, 0.1 + y * 2]}>
          <sphereGeometry args={[0.008, 6, 6]} />
          <meshBasicMaterial color="#FFD700" />
        </mesh>
      ))}
      {/* Tongue */}
      <mesh position={[0.15, 0.175, 0.08]} rotation={[0, 0, 0.1]}>
        <boxGeometry args={[0.04, 0.005, 0.005]} />
        <meshStandardMaterial color="#FF0000" />
      </mesh>
    </group>
  );
};

const Lizard = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <capsuleGeometry args={[0.04, 0.1, 6, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#6B8E23' : '#4a5a3a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.06, 0.1]} castShadow>
        <boxGeometry args={[0.05, 0.03, 0.06]} />
        <meshStandardMaterial color={world === 'real' ? '#6B8E23' : '#4a5a3a'} />
      </mesh>
      {/* Tail */}
      <mesh position={[0, 0.04, -0.12]} rotation={[0.2, 0, 0]}>
        <coneGeometry args={[0.025, 0.15, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#6B8E23' : '#4a5a3a'} />
      </mesh>
      {/* Legs */}
      {[[-0.05, 0.05], [0.05, 0.05], [-0.05, -0.05], [0.05, -0.05]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.02, z]} rotation={[0, 0, x > 0 ? 0.8 : -0.8]}>
          <cylinderGeometry args={[0.01, 0.008, 0.06, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#6B8E23' : '#4a5a3a'} />
        </mesh>
      ))}
      {/* Eyes */}
      {[-0.02, 0.02].map((x, i) => (
        <mesh key={i} position={[x, 0.075, 0.12]}>
          <sphereGeometry args={[0.008, 6, 6]} />
          <meshBasicMaterial color="#FFD700" />
        </mesh>
      ))}
    </group>
  );
};

export const desertAnimals: NPCModelRegistry = {
  camel: Camel,
  fennec: Fennec,
  scorpion: Scorpion,
  snake: Snake,
  lizard: Lizard,
};
